import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import {
  TrendingUp,
  Wallet,
  Receipt,
  Target,
  Trophy,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Banknote,
  Building2,
  CircleDollarSign,
  Eye,
  FileText,
  PieChart,
  Scale,
  ClipboardList,
} from "lucide-react";
import { axiosInstance } from "../App";
import { CurrencyDisplay } from "./CurrencySettings";
import { formatDateDDMonYYYY } from "../utils/dateFormat";
import DateFilter from "./DateFilter";
import AnomalyWidget from "./AnomalyWidget";

const ExecutiveDashboard = ({ 
  stats, 
  restaurants, 
  onFilterChange,
  startDate,
  endDate,
  groupBy,
  setGroupBy,
  onNavigate
}) => {
  const [cashBalance, setCashBalance] = useState(null);
  const [expenseData, setExpenseData] = useState({ total: 0, entries: [], byRestaurant: [] });
  const [handoverData, setHandoverData] = useState({ total: 0, count: 0, entries: [] });
  const [targetData, setTargetData] = useState({ targets: [], summary: { total: 0, achieved: 0, percentage: 0 } });
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState("all");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [trendView, setTrendView] = useState("daily"); // daily or monthly
  const [brands, setBrands] = useState([]);
  
  // Target month/year selection
  const [targetMonth, setTargetMonth] = useState(() => {
    const now = new Date();
    return String(now.getMonth() + 1).padStart(2, '0');
  });
  const [targetYear, setTargetYear] = useState(() => String(new Date().getFullYear()));
  
  // Popover/Dialog states
  const [netProfitOpen, setNetProfitOpen] = useState(false);
  const [cashBalanceOpen, setCashBalanceOpen] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [outletsOpen, setOutletsOpen] = useState(false);

  // Fetch brands on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await axiosInstance.get("/brands");
        setBrands(res.data || []);
      } catch (error) {
        console.error("Error fetching brands:", error);
      }
    };
    fetchBrands();
  }, []);

  // Create brand lookup map
  const brandLookup = brands.reduce((acc, b) => {
    acc[b.id] = b.name;
    return acc;
  }, {});

  // Get brand name from ID
  const getBrandName = (brandId) => {
    return brandLookup[brandId] || brandId;
  };

  // Get unique brands from restaurants with names
  const getUniqueBrands = () => {
    const brandIds = restaurants?.map(r => r.brand).filter(brand => brand && brand.trim() !== '') || [];
    const uniqueBrandIds = [...new Set(brandIds)];
    return uniqueBrandIds.map(id => ({ id, name: getBrandName(id) }));
  };

  // Filter stats based on selected restaurant and brand
  const getFilteredStats = () => {
    if (!stats) return null;
    
    let filteredBreakdown = stats.restaurant_breakdown || [];
    
    // Filter by restaurant
    if (selectedRestaurant !== "all") {
      filteredBreakdown = filteredBreakdown.filter(r => r.restaurant_id === selectedRestaurant);
    }
    
    // Filter by brand
    if (selectedBrand !== "all") {
      const restaurantIdsForBrand = restaurants
        ?.filter(r => r.brand === selectedBrand)
        .map(r => r.id) || [];
      filteredBreakdown = filteredBreakdown.filter(r => restaurantIdsForBrand.includes(r.restaurant_id));
    }
    
    const totalRevenue = filteredBreakdown.reduce((sum, r) => sum + (r.total || 0), 0);
    const totalEntries = filteredBreakdown.reduce((sum, r) => sum + (r.count || 0), 0);
    
    return {
      ...stats,
      total_revenue: totalRevenue,
      total_entries: totalEntries,
      restaurant_breakdown: filteredBreakdown,
      // Keep original date_wise_revenue for charts (or filter if needed)
      date_wise_revenue: stats.date_wise_revenue
    };
  };

  const filteredStats = getFilteredStats();

  useEffect(() => {
    fetchDashboardData();
  }, [startDate, endDate, selectedRestaurant, selectedBrand]);

  useEffect(() => {
    fetchTargetData();
  }, [targetMonth, targetYear]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const [cashRes, expenseRes, handoverRes] = await Promise.all([
        axiosInstance.get("/cash-ledger/balance").catch(() => ({ data: null })),
        axiosInstance.get(`/expense-entries?${params.toString()}`).catch(() => ({ data: [] })),
        axiosInstance.get("/cash-handover").catch(() => ({ data: [] })),
      ]);

      setCashBalance(cashRes.data);
      
      // Process expense data with restaurant grouping
      const expenses = expenseRes.data || [];
      const totalExpense = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      
      // Group expenses by restaurant
      const expenseByRestaurant = {};
      expenses.forEach(e => {
        const rId = e.restaurant_id || 'unknown';
        const rName = e.restaurant_name || 'Unknown';
        if (!expenseByRestaurant[rId]) {
          expenseByRestaurant[rId] = { restaurant_id: rId, restaurant_name: rName, total: 0, count: 0 };
        }
        expenseByRestaurant[rId].total += (e.amount || 0);
        expenseByRestaurant[rId].count += 1;
      });
      
      setExpenseData({ 
        total: totalExpense, 
        entries: expenses,
        byRestaurant: Object.values(expenseByRestaurant).sort((a, b) => b.total - a.total)
      });
      
      // Process handover data
      const handovers = handoverRes.data || [];
      const filteredHandovers = handovers.filter(h => {
        if (!startDate && !endDate) return true;
        const hDate = h.handover_date;
        if (startDate && hDate < startDate) return false;
        if (endDate && hDate > endDate) return false;
        return true;
      });
      const totalHandover = filteredHandovers.reduce((sum, h) => sum + (h.cash_handed_over || 0), 0);
      setHandoverData({ 
        total: totalHandover, 
        count: filteredHandovers.length,
        entries: filteredHandovers.sort((a, b) => new Date(b.handover_date) - new Date(a.handover_date))
      });
      
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTargetData = async () => {
    try {
      const targetRes = await axiosInstance.get("/restaurant-targets").catch(() => ({ data: [] }));
      const targets = targetRes.data || [];
      const selectedMonth = `${targetYear}-${targetMonth}`;
      const monthTargets = targets.filter(t => t.month === selectedMonth);
      const totalTarget = monthTargets.reduce((sum, t) => sum + (t.target_amount || 0), 0);
      const totalAchieved = monthTargets.reduce((sum, t) => sum + (t.achieved_amount || 0), 0);
      const achievedCount = monthTargets.filter(t => (t.achieved_amount || 0) >= (t.target_amount || 0)).length;
      
      setTargetData({
        targets: monthTargets,
        summary: {
          total: totalTarget,
          achieved: totalAchieved,
          percentage: totalTarget > 0 ? Math.round((totalAchieved / totalTarget) * 100) : 0,
          completedCount: achievedCount,
          totalCount: monthTargets.length
        }
      });
    } catch (error) {
      console.error("Error fetching targets:", error);
    }
  };

  const getTrendData = () => {
    if (!stats?.date_wise_revenue?.length) return [];
    
    // Filter date_wise_revenue by selected restaurant and brand
    let filteredData = stats.date_wise_revenue;
    
    // Filter by restaurant
    if (selectedRestaurant !== "all") {
      filteredData = filteredData.filter(item => item.restaurant_id === selectedRestaurant);
    }
    
    // Filter by brand
    if (selectedBrand !== "all") {
      const restaurantIdsForBrand = restaurants
        ?.filter(r => r.brand === selectedBrand)
        .map(r => r.id) || [];
      filteredData = filteredData.filter(item => restaurantIdsForBrand.includes(item.restaurant_id));
    }
    
    // Aggregate by date (since we now have per-restaurant entries)
    const dateAggregated = {};
    filteredData.forEach(item => {
      const dateKey = trendView === "monthly" ? item.date.substring(0, 7) : item.date;
      if (!dateAggregated[dateKey]) {
        dateAggregated[dateKey] = 0;
      }
      dateAggregated[dateKey] += item.total || 0;
    });
    
    // Convert to array and format
    return Object.entries(dateAggregated)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, revenue]) => ({
        date: trendView === "monthly" ? date : formatDateDDMonYYYY(date),
        revenue
      }));
  };

  const getTargetProgress = () => {
    return targetData.targets
      .map(t => ({
        ...t,
        percentage: t.target_amount > 0 ? Math.round((t.achieved_amount || 0) / t.target_amount * 100) : 0
      }))
      .sort((a, b) => b.percentage - a.percentage);
  };

  // Calculate net profit by restaurant
  const getNetProfitByRestaurant = () => {
    const revenueByRestaurant = {};
    filteredStats?.restaurant_breakdown?.forEach(r => {
      revenueByRestaurant[r.restaurant_id] = { 
        ...r, 
        revenue: r.total,
        expense: 0,
        netProfit: r.total 
      };
    });
    
    expenseData.byRestaurant?.forEach(e => {
      if (revenueByRestaurant[e.restaurant_id]) {
        revenueByRestaurant[e.restaurant_id].expense = e.total;
        revenueByRestaurant[e.restaurant_id].netProfit = 
          revenueByRestaurant[e.restaurant_id].revenue - e.total;
      }
    });
    
    return Object.values(revenueByRestaurant).sort((a, b) => b.netProfit - a.netProfit);
  };

  const topPerformers = [...(filteredStats?.restaurant_breakdown || [])]
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  const netPosition = (filteredStats?.total_revenue || 0) - expenseData.total;

  // Filter expenses based on selected restaurant/brand
  const getFilteredExpenses = () => {
    let filtered = expenseData.entries || [];
    
    if (selectedRestaurant !== "all") {
      filtered = filtered.filter(e => e.restaurant_id === selectedRestaurant);
    }
    
    if (selectedBrand !== "all") {
      const restaurantIdsForBrand = restaurants
        ?.filter(r => r.brand === selectedBrand)
        .map(r => r.id) || [];
      filtered = filtered.filter(e => restaurantIdsForBrand.includes(e.restaurant_id));
    }
    
    return filtered.reduce((sum, e) => sum + (e.amount || 0), 0);
  };

  const filteredExpenseTotal = getFilteredExpenses();
  const filteredNetPosition = (filteredStats?.total_revenue || 0) - filteredExpenseTotal;

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const years = ["2024", "2025", "2026", "2027"];

  return (
    <div className="space-y-4" data-testid="executive-dashboard">
      {/* Compact Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="flex-1 min-w-[180px] max-w-[220px]">
          <DateFilter
            label=""
            onFilterChange={(filterData) => {
              onFilterChange({
                startDate: filterData.startDate || "",
                endDate: filterData.endDate || "",
              });
            }}
          />
        </div>
        <Select value={selectedRestaurant} onValueChange={setSelectedRestaurant}>
          <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="filter-restaurant">
            <SelectValue placeholder="Restaurant" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Restaurants</SelectItem>
            {restaurants?.map((r) => (
              <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedBrand} onValueChange={setSelectedBrand}>
          <SelectTrigger className="w-[130px] h-9 text-sm" data-testid="filter-brand">
            <SelectValue placeholder="Brand" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Brands</SelectItem>
            {getUniqueBrands().map((brand) => (
              <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchDashboardData} className="h-9" data-testid="refresh-dashboard">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Data-Dense KPI Grid - 6 Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Revenue */}
        <Card className="border-l-4 border-l-emerald-500 shadow-sm" data-testid="kpi-revenue">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              <span className="text-[10px] font-semibold text-gray-500 uppercase">Revenue</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              <CurrencyDisplay amount={filteredStats?.total_revenue || 0} />
            </div>
            <div className="flex items-center gap-1 mt-1">
              <ArrowUpRight className="h-3 w-3 text-emerald-500" />
              <span className="text-[10px] text-emerald-600">+12%</span>
              <span className="text-[10px] text-gray-400 ml-1">{filteredStats?.total_entries || 0} entries</span>
            </div>
          </CardContent>
        </Card>

        {/* Expenses */}
        <Card className="border-l-4 border-l-rose-500 shadow-sm" data-testid="kpi-expenses">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="h-4 w-4 text-rose-500" />
              <span className="text-[10px] font-semibold text-gray-500 uppercase">Expenses</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              <CurrencyDisplay amount={filteredExpenseTotal} />
            </div>
            <div className="flex items-center gap-1 mt-1">
              <ArrowDownRight className="h-3 w-3 text-rose-500" />
              <span className="text-[10px] text-rose-600">-5%</span>
              <span className="text-[10px] text-gray-400 ml-1">{expenseData.entries?.length || 0} entries</span>
            </div>
          </CardContent>
        </Card>

        {/* Handover - Clickable with Dialog */}
        <Card 
          className="border-l-4 border-l-blue-500 shadow-sm cursor-pointer hover:shadow-md transition-shadow" 
          data-testid="kpi-handover"
          onClick={() => setHandoverOpen(true)}
        >
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Banknote className="h-4 w-4 text-blue-500" />
              <span className="text-[10px] font-semibold text-gray-500 uppercase">Handover</span>
              <Eye className="h-3 w-3 text-gray-400 ml-auto" />
            </div>
            <div className="text-lg font-bold text-gray-900">
              <CurrencyDisplay amount={handoverData.total} />
            </div>
            <div className="text-[10px] text-gray-400 mt-1">{handoverData.count} handovers • Click to view</div>
          </CardContent>
        </Card>

        {/* Cash Balance - Clickable with Dialog */}
        <Card 
          className="border-l-4 border-l-cyan-500 shadow-sm cursor-pointer hover:shadow-md transition-shadow" 
          data-testid="kpi-cash"
          onClick={() => setCashBalanceOpen(true)}
        >
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="h-4 w-4 text-cyan-500" />
              <span className="text-[10px] font-semibold text-gray-500 uppercase">Cash Bal.</span>
              <Eye className="h-3 w-3 text-gray-400 ml-auto" />
            </div>
            <div className="text-lg font-bold text-gray-900">
              <CurrencyDisplay amount={cashBalance?.current_balance || 0} />
            </div>
            <div className="text-[10px] text-gray-400 mt-1">Available • Click to view</div>
          </CardContent>
        </Card>

        {/* Net Profit - Clickable with Dialog */}
        <Card 
          className="border-l-4 border-l-teal-500 shadow-sm cursor-pointer hover:shadow-md transition-shadow" 
          data-testid="kpi-net"
          onClick={() => setNetProfitOpen(true)}
        >
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <CircleDollarSign className="h-4 w-4 text-teal-500" />
              <span className="text-[10px] font-semibold text-gray-500 uppercase">Net Profit</span>
              <Eye className="h-3 w-3 text-gray-400 ml-auto" />
            </div>
            <div className={`text-lg font-bold ${filteredNetPosition >= 0 ? 'text-teal-600' : 'text-rose-600'}`}>
              <CurrencyDisplay amount={filteredNetPosition} />
            </div>
            <div className="text-[10px] text-gray-400 mt-1">Rev - Exp • Click to view</div>
          </CardContent>
        </Card>

        {/* Outlets - Clickable with Dialog */}
        <Card 
          className="border-l-4 border-l-orange-500 shadow-sm cursor-pointer hover:shadow-md transition-shadow" 
          data-testid="kpi-restaurants"
          onClick={() => setOutletsOpen(true)}
        >
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="h-4 w-4 text-orange-500" />
              <span className="text-[10px] font-semibold text-gray-500 uppercase">Outlets</span>
              <Eye className="h-3 w-3 text-gray-400 ml-auto" />
            </div>
            <div className="text-lg font-bold text-gray-900">{filteredStats?.restaurant_breakdown?.length || 0}</div>
            <div className="text-[10px] text-gray-400 mt-1">Active • Click to view</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Revenue Trend Chart with Daily/Monthly Toggle */}
        <Card className="lg:col-span-2 shadow-sm" data-testid="chart-trend">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Revenue Trend</CardTitle>
              <div className="flex items-center gap-1">
                <Button 
                  variant={trendView === "daily" ? "default" : "outline"} 
                  size="sm" 
                  className="h-7 text-xs px-3"
                  onClick={() => setTrendView("daily")}
                  data-testid="trend-daily-btn"
                >
                  Daily
                </Button>
                <Button 
                  variant={trendView === "monthly" ? "default" : "outline"} 
                  size="sm" 
                  className="h-7 text-xs px-3"
                  onClick={() => setTrendView("monthly")}
                  data-testid="trend-monthly-btn"
                >
                  Monthly
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {!stats?.date_wise_revenue?.length ? (
              <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">
                No data for selected period
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={getTrendData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" stroke="#9CA3AF" fontSize={9} tickLine={false} />
                  <YAxis stroke="#9CA3AF" fontSize={9} tickLine={false} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ fontSize: "11px", borderRadius: "8px" }}
                    formatter={(value) => [<CurrencyDisplay amount={value} />, "Revenue"]}
                  />
                  <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Top Performers */}
        <Card className="shadow-sm" data-testid="chart-top-performers">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-amber-500" />
                <CardTitle className="text-sm font-semibold">Top Performers</CardTitle>
              </div>
              <Badge variant="outline" className="text-[10px]">{topPerformers.length}</Badge>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {!topPerformers.length ? (
              <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">
                No data available
              </div>
            ) : (
              <div className="space-y-3">
                {topPerformers.map((restaurant, index) => {
                  const percentage = filteredStats?.total_revenue > 0 
                    ? Math.round((restaurant.total / filteredStats.total_revenue) * 100) : 0;
                  return (
                    <div key={restaurant.restaurant_id} className="flex items-center gap-3">
                      <div className={`
                        w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                        ${index === 0 ? 'bg-amber-100 text-amber-700' : 
                          index === 1 ? 'bg-gray-100 text-gray-600' :
                          index === 2 ? 'bg-orange-100 text-orange-700' : 
                          'bg-gray-50 text-gray-500'}
                      `}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-700 truncate">
                            {restaurant.restaurant_name}
                          </span>
                          <span className="text-xs font-bold text-gray-900 ml-2">
                            <CurrencyDisplay amount={restaurant.total} />
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress value={percentage} className="h-1.5 flex-1" />
                          <span className="text-[10px] text-gray-500 w-8">{percentage}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick View Hero Buttons */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="quick-views">
        {/* P&L Report */}
        <Card 
          className="shadow-sm cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200"
          onClick={() => onNavigate?.('pl-report')}
          data-testid="quick-view-pl"
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <PieChart className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">P&L Report</p>
              <p className="text-xs text-gray-500">Profit & Loss</p>
            </div>
          </CardContent>
        </Card>

        {/* Target Analytics */}
        <Card 
          className="shadow-sm cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200"
          onClick={() => onNavigate?.('target-analytics')}
          data-testid="quick-view-targets"
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-violet-100 rounded-xl">
              <Target className="h-6 w-6 text-violet-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Target Analytics</p>
              <p className="text-xs text-gray-500">Performance</p>
            </div>
          </CardContent>
        </Card>

        {/* Cash Reconciliation */}
        <Card 
          className="shadow-sm cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200"
          onClick={() => onNavigate?.('cash-reconciliation')}
          data-testid="quick-view-cash"
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-emerald-100 rounded-xl">
              <Scale className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Cash Reconciliation</p>
              <p className="text-xs text-gray-500">Balance Check</p>
            </div>
          </CardContent>
        </Card>

        {/* Expense Summary */}
        <Card 
          className="shadow-sm cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"
          onClick={() => onNavigate?.('expense-summary')}
          data-testid="quick-view-expense"
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-xl">
              <ClipboardList className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Expense Summary</p>
              <p className="text-xs text-gray-500">Cost Overview</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Anomaly Widget */}
      <AnomalyWidget onViewAll={() => onNavigate?.('anomalies')} />

      {/* Restaurant Performance Chart */}
      <Card className="shadow-sm" data-testid="chart-comparison">
        <CardHeader className="py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-500" />
              <CardTitle className="text-sm font-semibold">Revenue by Restaurant</CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {!filteredStats?.restaurant_breakdown?.length ? (
            <div className="h-[180px] flex items-center justify-center text-gray-400 text-sm">
              No data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart 
                data={[...filteredStats.restaurant_breakdown].sort((a, b) => b.total - a.total)}
                layout="vertical"
                margin={{ left: 0, right: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={true} vertical={false} />
                <XAxis type="number" fontSize={9} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                <YAxis 
                  type="category" 
                  dataKey="restaurant_name" 
                  fontSize={9} 
                  width={80}
                  tickFormatter={(v) => v.length > 12 ? v.slice(0, 12) + '...' : v}
                />
                <Tooltip
                  contentStyle={{ fontSize: "11px", borderRadius: "8px" }}
                  formatter={(value) => [<CurrencyDisplay amount={value} />, "Revenue"]}
                />
                <Bar dataKey="total" fill="#3B82F6" radius={[0, 4, 4, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Handover Dialog - Centered */}
      <Dialog open={handoverOpen} onOpenChange={setHandoverOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="h-5 w-5 text-blue-500" />
              Handover Details
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 -mt-2">All handovers in selected period</p>
          <div className="max-h-[350px] overflow-y-auto border rounded-lg">
            {handoverData.entries.length === 0 ? (
              <div className="p-6 text-center text-gray-400">No handovers found</div>
            ) : (
              <div className="divide-y">
                {handoverData.entries.map((h, idx) => (
                  <div key={idx} className="p-3 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{h.handed_over_by_name || h.handed_over_by || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">To: {h.handed_over_to || '-'}</p>
                        <p className="text-xs text-gray-400">{formatDateDDMonYYYY(h.handover_date)}</p>
                      </div>
                      <span className="text-lg font-bold text-blue-600">
                        <CurrencyDisplay amount={h.cash_handed_over || 0} />
                      </span>
                    </div>
                    {h.remarks && <p className="text-sm text-gray-400 mt-1">{h.remarks}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="font-semibold">Total Handover</span>
            <span className="text-xl font-bold text-blue-600"><CurrencyDisplay amount={handoverData.total} /></span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash Balance Dialog - Centered */}
      <Dialog open={cashBalanceOpen} onOpenChange={setCashBalanceOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-cyan-500" />
              Cash Balance Breakdown
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 -mt-2">Revenue by restaurant</p>
          <div className="max-h-[350px] overflow-y-auto border rounded-lg">
            {!filteredStats?.restaurant_breakdown?.length ? (
              <div className="p-6 text-center text-gray-400">No data available</div>
            ) : (
              <div className="divide-y">
                {filteredStats.restaurant_breakdown.map((r, idx) => (
                  <div key={idx} className="p-3 hover:bg-gray-50">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{r.restaurant_name}</span>
                      <span className="text-lg font-bold text-cyan-600">
                        <CurrencyDisplay amount={r.total || 0} />
                      </span>
                    </div>
                    <p className="text-sm text-gray-400">{r.count} entries</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="font-semibold">Current Balance</span>
            <span className="text-xl font-bold text-cyan-600"><CurrencyDisplay amount={cashBalance?.current_balance || 0} /></span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Net Profit Dialog - Centered */}
      <Dialog open={netProfitOpen} onOpenChange={setNetProfitOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CircleDollarSign className="h-5 w-5 text-teal-500" />
              Net Profit by Restaurant
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 -mt-2">Revenue - Expenses breakdown</p>
          <div className="max-h-[350px] overflow-y-auto border rounded-lg">
            {getNetProfitByRestaurant().length === 0 ? (
              <div className="p-6 text-center text-gray-400">No data available</div>
            ) : (
              <div className="divide-y">
                {getNetProfitByRestaurant().map((r, idx) => (
                  <div key={idx} className="p-3 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium">{r.restaurant_name}</span>
                      <span className={`text-lg font-bold ${r.netProfit >= 0 ? 'text-teal-600' : 'text-rose-600'}`}>
                        <CurrencyDisplay amount={r.netProfit} />
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Revenue: <CurrencyDisplay amount={r.revenue || 0} /></span>
                      <span>Expenses: <CurrencyDisplay amount={r.expense || 0} /></span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="font-semibold">Total Net Profit</span>
            <span className={`text-xl font-bold ${filteredNetPosition >= 0 ? 'text-teal-600' : 'text-rose-600'}`}>
              <CurrencyDisplay amount={filteredNetPosition} />
            </span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Outlets Dialog - Centered */}
      <Dialog open={outletsOpen} onOpenChange={setOutletsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-orange-500" />
              Outlets Overview
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 -mt-2">Active restaurants with revenue</p>
          <div className="max-h-[400px] overflow-y-auto border rounded-lg">
            {!filteredStats?.restaurant_breakdown?.length ? (
              <div className="p-6 text-center text-gray-400">No outlets with revenue data</div>
            ) : (
              <div className="divide-y">
                {[...filteredStats.restaurant_breakdown].sort((a, b) => b.total - a.total).map((r, idx) => {
                  const restaurant = restaurants?.find(rest => rest.id === r.restaurant_id);
                  const brandName = restaurant?.brand ? getBrandName(restaurant.brand) : '-';
                  return (
                    <div key={idx} className="p-3 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-1">
                        <div>
                          <span className="font-medium">{r.restaurant_name}</span>
                          <p className="text-xs text-gray-400">Brand: {brandName}</p>
                        </div>
                        <span className="text-lg font-bold text-orange-600">
                          <CurrencyDisplay amount={r.total || 0} />
                        </span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>{r.count} entries</span>
                        <span>Avg: <CurrencyDisplay amount={r.count > 0 ? r.total / r.count : 0} /></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="font-semibold">Total Outlets</span>
            <span className="text-xl font-bold text-orange-600">{filteredStats?.restaurant_breakdown?.length || 0}</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExecutiveDashboard;
